/**
 * Mapeo de tarjetas guardadas → items del carrusel. El id de cada item es posicional
 * (`card-<n>`): el token nunca llega al DOM.
 */
import type { PaymentMethodItem } from '../components/PaymentMethodCarousel/types';
import type { SavedCard } from '../domain/models';
import type { CardDesignResolver } from '../infrastructure/cardDesignResolver';
import { isDesktop } from '../platform';

export const CARD_ID_PREFIX = 'card-';
export const MERCADOPAGO_ID = 'mercadopago';

export interface PaymentMethodsModel {
  items: PaymentMethodItem[];
  cardsById: Record<string, SavedCard>;
}

export const isCardId = (id: string | undefined): id is string =>
  id !== undefined && id.startsWith(CARD_ID_PREFIX);

/** MM/AA a partir del vencimiento de la tarjeta (el año puede venir de 2 o de 4 dígitos). */
const formatExpiry = (card: SavedCard): string | undefined =>
  card.expirationMonth && card.expirationYear
    ? `${card.expirationMonth.padStart(2, '0')}/${card.expirationYear.slice(-2)}`
    : undefined;

export const toPaymentMethods = (
  cards: SavedCard[],
  resolver: CardDesignResolver,
  /** Mercado Pago se ofrece solo si el GET de tarjetas lo informa habilitado. */
  mercadoPagoEnabled = false,
): PaymentMethodsModel => {
  const usable = cards.filter((card) => !card.expired);

  const entries = usable.map((card, index) => {
    const id = `${CARD_ID_PREFIX}${index + 1}`;
    const design = resolver.resolve(card);
    // Solo las tarjetas nuevas de la sesión muestran titular y vencimiento (diseño "Tarjeta guardada con color").
    const isSession = card.paymentTokenCreatedAt !== undefined;
    const item: PaymentMethodItem = {
      kind: 'card',
      id,
      issuerName: design.issuerName,
      last4: card.lastFourDigits,
      holderName: isSession ? (card.holderName ?? '') : '',
      ...(isSession ? { expiry: formatExpiry(card) } : {}),
      network: design.network,
      networkVariant: design.networkVariant,
      gradientColors: design.gradientColors,
      ...(design.issuer ? { issuer: design.issuer } : {}),
    };
    return { id, card, item };
  });

  const addItem: PaymentMethodItem = { kind: 'add' };

  return {
    // "Agregar tarjeta" va primero solo en desktop; en la app, al final.
    items: [
      ...(isDesktop ? [addItem] : []),
      ...entries.map((entry) => entry.item),
      ...(mercadoPagoEnabled ? [{ kind: 'mercadopago' as const, id: MERCADOPAGO_ID }] : []),
      ...(isDesktop ? [] : [addItem]),
    ],
    cardsById: Object.fromEntries(entries.map((entry) => [entry.id, entry.card])),
  };
};
